/**
 * Sandbox Artifact Service
 * Fetches manifest and files from sandbox artifacts (CloudFront routes to S3).
 * Manifest: https://{first5UserId}--{first5ProjectId}.sandbox.vishmaker.com/artifacts/manifest.json
 * Raw file: https://{first5UserId}--{first5ProjectId}.sandbox.vishmaker.com/artifacts/raw/{file_path}
 */

export interface ManifestEntry {
  path: string;
  /** Some pipelines omit this; treat non-`'dir'` paths with a file extension as files. */
  type?: 'dir' | 'file' | string;
  size?: number;
}

export interface Manifest {
  version: number;
  repoType: string;
  generatedAt: string;
  entries: ManifestEntry[];
}

/**
 * Get the sandbox base URL from userId and projectId (CloudFront domain, routes to S3)
 */
export function getSandboxBaseUrl(
  userId: string | null | undefined,
  projectId: string | number | null | undefined
): string | null {
  if (userId == null || userId === '' || projectId == null || projectId === '') return null;

  const userIdStr = String(userId).trim();
  const projectIdStr = String(projectId).trim();
  if (!userIdStr || !projectIdStr) return null;
  
  const first5UserId = userIdStr.length >= 5 
    ? userIdStr.slice(0, 5) 
    : userIdStr.padStart(5, '0');
  const first5ProjectId = projectIdStr.length >= 5 
    ? projectIdStr.slice(0, 5) 
    : projectIdStr.padStart(5, '0');
  
  return `https://${first5UserId}--${first5ProjectId}.sandbox.vishmaker.com`;
}

/**
 * Fetch manifest.json from sandbox artifacts
 */
export async function fetchManifest(baseUrl: string): Promise<Manifest> {
  const manifestUrl = `${baseUrl}/artifacts/manifest.json`;

  try {
    const response = await fetch(manifestUrl);

    if (!response.ok) {
      const msg = `Failed to fetch manifest: ${response.status} ${response.statusText}`;
      console.error('[Sandbox]', msg, { manifestUrl });
      throw new Error(msg);
    }

    const manifest: Manifest = await response.json();

    // Validate manifest structure
    if (!manifest.version || !Array.isArray(manifest.entries)) {
      console.error('[Sandbox] Invalid manifest structure', manifest);
      throw new Error('Invalid manifest structure');
    }

    return manifest;
  } catch (error) {
    console.error('[Sandbox] Manifest fetch failed', { manifestUrl, error });
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(`Network error: Unable to reach ${baseUrl}. Please check if the sandbox is available.`);
    }
    throw error;
  }
}

/**
 * Fetch a file from sandbox artifacts using /artifacts/raw/{file_path}
 */
export async function fetchArtifactFile(baseUrl: string, path: string): Promise<string> {
  // Ensure path doesn't start with / to avoid double slashes
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const fileUrl = `${baseUrl}/artifacts/raw/${cleanPath}`;
  
  try {
    const response = await fetch(fileUrl);

    if (!response.ok) {
      const msg = `Failed to fetch file: ${response.status} ${response.statusText}`;
      console.error('[Sandbox]', msg, { fileUrl });
      throw new Error(msg);
    }

    return await response.text();
  } catch (error) {
    console.error('[Sandbox] File fetch failed', { fileUrl, path, error });
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(`Network error: Unable to fetch ${path}. Please check if the sandbox is available.`);
    }
    throw error;
  }
}

/**
 * Build a hierarchical file tree from manifest entries
 */
export interface FileTreeNode {
  name: string;
  path: string;
  type: 'file' | 'dir';
  children?: FileTreeNode[];
  entry?: ManifestEntry; // Original entry for files
}

export function buildFileTree(entries: ManifestEntry[]): FileTreeNode[] {
  // Use a temporary Map structure to build the tree efficiently
  const rootMap: Map<string, { node: FileTreeNode; children: Map<string, unknown> }> = new Map();

  for (const entry of entries) {
    const parts = entry.path.split('/').filter(Boolean);
    let currentLevel = rootMap;
    let currentPath = '';

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isLast = i === parts.length - 1;
      const entryType = entry.type === 'file' ? 'file' : 'dir';
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      if (!currentLevel.has(part)) {
        const node: FileTreeNode = {
          name: part,
          path: currentPath,
          type: isLast ? entryType : 'dir',
          children: isLast && entryType === 'file' ? undefined : []
        };

        if (isLast) {
          node.entry = entry;
        }

        currentLevel.set(part, {
          node,
          children: isLast ? new Map() : new Map()
        });
      }

      const item = currentLevel.get(part)!;

      if (isLast) {
        item.node.entry = entry;
      } else {
        currentLevel = item.children as Map<string, { node: FileTreeNode; children: Map<string, unknown> }>;
      }
    }
  }
  
  // Convert Maps to arrays recursively
  const convertToArray = (map: Map<string, { node: FileTreeNode; children: Map<string, unknown> }>): FileTreeNode[] => {
    const result: FileTreeNode[] = [];
    for (const [name, item] of map.entries()) {
      if (item.node.type === 'dir' && item.children.size > 0) {
        item.node.children = convertToArray(item.children as Map<string, { node: FileTreeNode; children: Map<string, unknown> }>);
      }
      result.push(item.node);
    }
    return result.sort((a, b) => {
      // Directories first, then files, then alphabetical
      if (a.type !== b.type) {
        return a.type === 'dir' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  };
  
  return convertToArray(rootMap);
}

/**
 * Derive kind from entry path for filtering:
 * - path starts with 'infra' → 'arch'
 * - path starts with 'docs' → 'docs'
 * - path starts with 'app' → 'code'
 */
export function getEntryKindFromPath(path: string): 'arch' | 'docs' | 'code' | null {
  const p = path.startsWith('/') ? path.slice(1) : path;
  if (p === 'infra' || p.startsWith('infra/')) return 'arch';
  if (p === 'docs' || p.startsWith('docs/')) return 'docs';
  if (p === 'app' || p.startsWith('app/')) return 'code';
  return null;
}

/**
 * Filter entries by kind, where kind is derived from path prefix:
 * - 'arch' → path starts with 'infra'
 * - 'docs' → path starts with 'docs'
 * - 'code' → path starts with 'app'
 * Also accepts 'doc' as alias for 'docs' for backward compatibility.
 */
export function filterEntriesByKind(entries: ManifestEntry[], kind: string): ManifestEntry[] {
  const normalizedKind = kind === 'doc' ? 'docs' : kind;
  return entries.filter(entry => getEntryKindFromPath(entry.path) === normalizedKind);
}

/**
 * Filter entries to files only (exclude directories)
 */
export function filterFileEntries(entries: ManifestEntry[]): ManifestEntry[] {
  return entries.filter(entry => entry.type === 'file');
}

/** Paths treated as documentation in the Documents tab (sandbox manifest). */
export function isDocumentFilePath(path: string): boolean {
  const lower = path.toLowerCase();
  return lower.endsWith('.md') || lower.endsWith('.docx');
}

/**
 * Manifest file entries that are documentation (.md, .docx).
 * Matches Code tab behavior: include paths that look like docs even when `type` is missing
 * (some manifests only set `type` for directories or omit it for files).
 * Normalizes to `type: 'file'` so {@link buildFileTree} does not treat leaves as directories.
 */
export function filterDocumentEntries(entries: ManifestEntry[]): ManifestEntry[] {
  return entries
    .filter((e) => {
      if (!e?.path || !isDocumentFilePath(e.path)) return false;
      const t = e.type;
      if (t === 'dir' || String(t).toLowerCase() === 'dir') return false;
      return true;
    })
    .map(
      (e): ManifestEntry => ({
        ...e,
        type: 'file'
      })
    );
}

/**
 * Fetch binary file from sandbox (e.g. .docx); use instead of fetchArtifactFile which uses text().
 */
export async function fetchArtifactBlob(baseUrl: string, path: string): Promise<Blob> {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const fileUrl = `${baseUrl}/artifacts/raw/${cleanPath}`;

  try {
    const response = await fetch(fileUrl);

    if (!response.ok) {
      const msg = `Failed to fetch file: ${response.status} ${response.statusText}`;
      console.error('[Sandbox]', msg, { fileUrl });
      throw new Error(msg);
    }

    return await response.blob();
  } catch (error) {
    console.error('[Sandbox] Blob fetch failed', { fileUrl, path, error });
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error(`Network error: Unable to fetch ${path}. Please check if the sandbox is available.`);
    }
    throw error;
  }
}
